<template>
  <StandardLayout style="text-align: center;">
    <!-- Drill-down popup -->
    <ModalPopup
        v-if="displayDrillDownPopup"
        width="75%"
        :title="popupTitle"
        :component="popupComponent"
        :componentArgs="popupComponentArgs"
        v-on:closePopup="displayDrillDownPopup=false">
    </ModalPopup>
    <NewPanelPopup
        v-if="displayNewPanelPopup"
        @close="displayNewPanelPopup=false"
        @save="addNewPanel"
        title="Add new dashboard"
        :panelName.sync="newPanelName"/>
    <DuplicatePanelPopup
        v-if="displayDuplicatePanelPopup"
        @save="duplicatePanel()"
        @close="() => {displayDuplicatePanelPopup=false}"
        :panelName.sync="duplicatePanelName"/>
    <PanelDeleteConfirmPopUp
        v-if="displayPanelDeleteConfirmPopup"
        :panel-name="currentPanel.name"
        @confirm="delPanel()"
        @close="displayPanelDeleteConfirmPopup=false"/>
    <WidgetSettings
        v-if="displayWidgetSettings"
        :widgetConfig.sync="widgetSettingsConfig"
        @save="saveWidgetSettings"
        @close="displayWidgetSettings=false"/>
    <WidgetSettings
        v-if="displayGlobalParams"
        :widgetConfig.sync="widgetGlobalConfig"
        :global="true"
        @save="saveGlobalParams"
        @close="displayGlobalParams=false"/>
    <SharePanelPopup
        v-if="displaySharePanelPopup"
        :panel.sync="currentPanel"
        :canEdit="isOwnPanel(currentPanel)"
        :canShare="currentPanel.canShare"
        @close="displaySharePanelPopup=false"/>
    <div ref="widthIndicator" id="widthIndicator" style="width: 100%; overflow: auto;"></div>
    <div ref="dndGrid" v-bind:style="{width: `${gridWidth}px`, overflow: 'auto', 'height': '100%', 'overflow-x': 'hidden', 'scrollbar-width': 'none'}" id="dndGrid">
      <h3 style="text-align: left; margin-left: 5pt;">{{currentPanel.name}}</h3>
      <h4 v-if="!panels" style="margin-top: 10%;">{{panelsErrorMessage}}</h4>
      <LoadingSpinner v-if="panelLoading"/>
      <grid-layout
          v-if="!panelLoading && panels"
          :style="{width: `${gridWidth}px`}"
          :layout.sync="layout"
          :row-height="rowHeight"
          :col-width="colWidth"
          :col-num="gridColNum"
          :is-draggable="toggleConfig"
          :is-resizable="toggleConfig"
          :is-mirrored="false"
          :auto-size="false"
          :prevent-collision="false"
          :vertical-compact="false"
          :margin="gridMargins"
          :use-css-transforms="true"
          :responsive="false"
          @layout-ready="layoutReadyEvent"
          @layout-updated="layoutUpdatedEvent">
        <grid-item v-for="(item, idx) in layout"
                   :x="item.x"
                   :y="item.y"
                   :w="item.w"
                   :h="item.h"
                   :i="item.i"
                   :key="item.i + '_' + item.wc.widgetName">
          <component v-if="validWidget(item.wc.widgetName, idx)"
                     @destroy="deleteWidget(idx)"
                     @settings="openWidgetSettings(idx)"
                     :is="item.widget"
                     :config="item"
                     @config-update="(i) => configUpdate(idx, i)"/>
        </grid-item>
      </grid-layout>
    </div>
    <template v-slot:sideNavSubmenu>
      <li v-for="(p, idx) in panelsSorted" :class="{
       menuBorderBottom: idx+1 === panelsSorted.length,
       asideNavigation_item: true,
       selected: String(p.id) === $route.params.id,
      }">
        <a class="asideNavigation_link" style="position: relative; padding-right: 40px;" @click="switchPanel(p.id)"><div>{{ p.name }}</div>
          <div v-if="isOwnPanel(p) && (!isPublicPanel(p) && !isSharedWithUsersPanel(p))" class="menuIcon privatePanel" :title="`Owner ${$store.state.userInfo.userName}, this dashboard is private`"></div>
          <div v-if="!isOwnPanel(p)" class="menuIcon sharedPanel" :title="`Owned by ${p.ownerInfo.forename} ${p.ownerInfo.surname} <${p.ownerInfo.username}>`" :fn="getPanelUserName(p.id)"></div>
          <div v-if="isPublicPanel(p)" class="menuIcon publicPanel" title="Public"></div>
          <div v-if="isSharedWithUsersPanel(p)" class="menuIcon sharedWithUsersPanel" title="Shared with users"></div>
        </a>
      </li>
    </template>
    <template v-slot:sideNavOptions>
      <ul class="asideOptions" style="padding-top: 0.2em;">
        <li v-show="isOwnPanel()">
          <ToggleSwitch :selected.sync="toggleConfig" :buttons="toggleButtonsConfig"/>
        </li>
        <li class="asideOptions_item" @click="displayNewPanelPopup=true"><a class="asideOptions_link">Add dashboard
          <div class="addIcon icon"></div>
        </a></li>
        <li v-if="currentPanel.canShare && isOwnPanel(currentPanel)" class="asideOptions_item" @click="displaySharePanelPopup=true"><a class="asideOptions_link">Edit / Share dashboard
          <div class="shareIcon icon"></div>
        </a></li>
        <li v-else-if="currentPanel.canShare" class="asideOptions_item" @click="displaySharePanelPopup=true"><a class="asideOptions_link">Share dashboard
          <div class="shareIcon icon"></div>
        </a></li>
        <li v-else-if="isOwnPanel(currentPanel)" class="asideOptions_item" @click="displaySharePanelPopup=true"><a class="asideOptions_link">Edit dashboard
          <div class="shareIcon icon"></div>
        </a></li>
        <li class="asideOptions_item" @click="() => {duplicatePanelName = currentPanel.name; displayDuplicatePanelPopup=true}">
          <a class="asideOptions_link">Duplicate dashboard<div class="duplicatePanelIcon icon"></div></a>
        </li>
        <li v-if="toggleConfig && currentPanel.canDelete" class="asideOptions_item" @click="displayPanelDeleteConfirmPopup=true"><a class="asideOptions_link">Delete dashboard
            <div class="deleteIcon icon"></div>
          </a></li>
      </ul>
    </template>
    <template v-slot:sideNavWidgets>
      <WidgetMenu
          v-if="toggleConfig"
          :widgets="widgets"
          :dnd-pos="dnd"
          @start-drag="startDrag"
          @stop-drag="stopDrag"/>
    </template>
    <div id="widgetDragPlaceholder"></div>
  </StandardLayout>
</template>

<script>
import {bus} from "@/main"
import VueGridLayout from 'vue-grid-layout'
import * as WF from '@/components/widgets/WidgetFather'
import WidgetMenu from '@/components/WidgetMenu.vue'
import ModalPopup from '@/components/dialogs/ModalPopup.vue'
import ToggleSwitch from '@/components/buttons/ToggleSwitch.vue'
import {duplicatePanel, panelCreate, panelDelete, panelsList, ShareMode} from "@/data/repos/panels"
import {widgetInstancesList, widgetInstancesUpdate} from "@/data/repos/widgetInstances"
import StandardLayout from '@/components/layout/standard/StandardLayout.vue'
import NewPanelPopup from '@/components/dialogs/NewPanelPopup.vue'
import DNDPlaceholder from '@/components/widgets/DNDPlaceholder.vue'
import PanelDeleteConfirmPopUp from '@/components/dialogs/PanelDeleteConfirmPopUp.vue'
import WidgetSettings from '@/components/dialogs/WidgetSettings.vue'
import SharePanelPopup from '@/components/dialogs/SharePanelPopup.vue'
import LoadingSpinner from "@/components/LoadingSpinner"
import {deepMerge, mergeWidgetConfig} from "@/utils"
import {getUser} from "@/data/repos/authUser"
import DuplicatePanelPopup from "../components/dialogs/DuplicatePanelPopup";
import {widgetByName} from "@/data/sources/widgetResolver";
import config from "@/config"
import {globalParams, saveGlobalParams} from "@/components/widgets/WidgetMother";

export default {
    name: 'Dashboards',
    components: {
      DuplicatePanelPopup,
      LoadingSpinner,
      StandardLayout,
      GridLayout: VueGridLayout.GridLayout,
      GridItem: VueGridLayout.GridItem,
      DNDPlaceholder,
      NewPanelPopup,
      SharePanelPopup,
      WidgetSettings,
      WidgetMenu,
      ToggleSwitch,
      ModalPopup,
      PanelDeleteConfirmPopUp,
    },
    props: {
      id: String,
    },
    data() {
      return {
        layout: [],
        gridWidth: WF.Grid.cellWidth * 10,
        gridColNum: 6,
        colWidth: WF.Grid.cellWidth,
        rowHeight: WF.Grid.cellHeight,
        gridMargins: [WF.Grid.marginX, WF.Grid.marginY],
        popupTitle: '',
        popupComponent: '',
        popupComponentArgs: null,
        displayDrillDownPopup: false,
        displayNewPanelPopup: false,
        displayDuplicatePanelPopup: false,
        displayWidgetSettings: false,
        displaySharePanelPopup: false,
        displayPanelDeleteConfirmPopup: false,
        widgetSettingsConfig: {},
        widgetSettingsIdx: 0,
        newPanelName: '',
        duplicatePanelName: '',
        toggleConfig: false,
        toggleButtonsConfig: [
          {
            value: false,
            title: 'View'
          },
          {
            value: true,
            title: 'Configure'
          },
        ],
        user: {id: 550},
        widgets: this.filterWidgets(WF.Widgets),
        panels: {},
        panelsErrorMessage: undefined,
        currentPanel: {},
        dnd: {x: 0, y: 0},
        dndWidget: null,
        dndWidgetIdx: -1,
        dndFinalWidget: null,
        panelLoading: false,
        displayGlobalParams: false,
        widgetGlobalConfig: {},
      }
    },
    computed: {
      widgetMap() {
        let wh = {}
        for (const cat of this.widgets) {
          cat.widgets.reduce(function (result, item) {
            result[item.widget] = item
            return result
          }, wh)
        }
        wh['DNDPlaceholder'] = DNDPlaceholder
        return wh
      },
      panelsSorted() {
        const panelSort = (x, y) => {
          if (this.isOwnPanel(x) === this.isOwnPanel(y)) {
            if (x.name.toLocaleUpperCase() > y.name.toLocaleUpperCase()) return 1
            if (x.name.toLocaleUpperCase() < y.name.toLocaleUpperCase()) return -1
            return 0
          }
          if (this.isOwnPanel(x) > this.isOwnPanel(y)) return -1
          if (this.isOwnPanel(x) < this.isOwnPanel(y)) return 1
          return 0
        }
        return Object.values(this.panels).sort(panelSort)
      }
    },
    watch: {
      widgetSettingsConfig: {
        handler(val) {
          this.layout[this.widgetSettingsIdx] = val
        }, deep: true,
      },
      currentPanel(p) {
        if (p.id in this.panels) {
          this.panels[p.id] = p
        }
      },
      dnd: {
        handler(val) {
          this.dndWidget.x = Math.floor(val.x / WF.Grid.cellWidth)
          this.dndWidget.y = Math.floor(val.y / WF.Grid.cellHeight)
        }, deep: true,
      },
      panels() {
        // switch to the first available panel if current panel not in the panel list
        if (this.panels && !(parseInt(this.id) in this.panels)) {
          if (Object.keys(this.panels).length > 0) {
            this.switchPanel(this.panelsSorted[0].id)
          }
        }
      },
      id(id) {
        this.loadWidgetsLayout(id)
        this.updateGrid()
      },
      layout() {
        // this.$toasted.info('layout changed')
        this.updateGrid()
      },
      toggleConfig(v) {
        bus.$emit('dashboard-config', v)
      },
    },
    created() {
      // register chartPopup event listener on a bus to display popup
      bus.$on('chartPopup', (data) => {
        this.popupTitle = data.title
        this.popupComponent = data.component
        this.popupComponentArgs = data.componentArgs
        this.displayDrillDownPopup = true
      })
      bus.$on('showGlobalSettings', () => this.showGlobalParams())

      this.loadPanels() // load panels to the left menu
      window.addEventListener('resize', this.updateGrid)
    },
    mounted() {
    },
    destroyed() {
      window.removeEventListener('resize', this.updateGrid)
    },
    methods: {
      async getPanelUserName(id) {
        if(!this.panels[id].ownerInfo.username) {
          this.panels[id].ownerInfo = await getUser(this.panels[id].userId)
        }
      },
      configUpdate(idx, i) {
        mergeWidgetConfig(this.layout[idx], i)
      },
      isOwnPanel(panel) {
        if (this.$store.state.userInfo.userId === 0) {
          return true
        }
        if (panel && panel.userId) {
          return this.$store.state.userInfo.userId === panel.userId
        } else {
          return this.$store.state.userInfo.userId === this.currentPanel.userId
        }
      },
      isPublicPanel(panel) {
        return (this.isOwnPanel(panel) && panel.share === ShareMode.public)
      },
      isSharedWithUsersPanel(panel) {
        return (this.isOwnPanel(panel) && panel.share === ShareMode.users)
      },
      startDrag(widgetName) {
        this.dndFinalWidget = this.widgetMap[widgetName]
        let pw = WF.DNDPlaceholder
        this.dndWidget = this.createWidget(this.dndFinalWidget, {x: -10, y: -10})
        this.dndWidget.wc.widgetName = pw.widget
        this.dndWidgetIdx = this.layout.push(this.dndWidget)
      },
      stopDrag() {
        let index = this.layout.indexOf(this.dndWidget);
        if (index > -1) {
          if (this.layout[index].x < 0 || this.layout[index].y < 0) {
            // remove widget if it was dropped off grid
            this.layout.splice(index, 1)
          } else {
            this.layout[index].wc.widgetName = this.dndFinalWidget.widget
          }
        }
        this.dndWidgetIdx = -1
        this.dndWidget = null
        this.$nextTick(() => {
          bus.$emit('dashboard-config', true)
          this.saveWidgetsLayout()
        })
      },
      openWidgetSettings(idx) {
        this.widgetSettingsIdx = idx
        this.widgetSettingsConfig = this.layout[idx]
        this.displayWidgetSettings = true
      },
      saveWidgetSettings() {
        this.displayWidgetSettings = false
        this.saveWidgetsLayout()
      },
      filterWidgets(widgets) {
        return config.hasOwnProperty("CHART_CATEGORIES") ?
            widgets.filter(wc => config.CHART_CATEGORIES.indexOf(wc.category) > -1)
            : widgets;
      },
      validWidget(widgetName, idx) {
        if (this.widgetMap[widgetName] === undefined) {
          console.log('invalid widget:', widgetName)
          this.deleteWidget(idx)
        }
        return true
      },
      deleteWidget(idx) {
        this.layout.splice(idx, 1)
        this.saveWidgetsLayout()
      },
      createWidget(w, pos) {
        let widget = JSON.parse(JSON.stringify(w))
        widget.widget = widgetByName(w.widget)
        widget.i = this.layout.length + w.widget
        widget.x = pos.x || 0
        widget.y = pos.y || 0
        widget.theme = w.theme || WF.FusionThemesDefault
        widget.wc = {widgetName: w.widget, panelId: parseInt(this.id)}
        return widget
      },
      layoutReadyEvent() {
      },
      async layoutUpdatedEvent() {
        if (this.toggleConfig) {
          await this.saveWidgetsLayout()
        }
      },
      updateGrid() {
        let minWidth = this.colWidth * 8
        let minHeight = this.rowHeight * 4
        let extraW = 2
        let extraH = 2
        if (this.layout !== undefined) {
          for (let i of this.layout) {
            let w = (i.x + i.w + extraW) * this.colWidth
            let h = (i.y + i.h + extraH) * this.rowHeight
            if (w > minWidth) {
              minWidth = w
            }
            if (h > minHeight) {
              minHeight = h
            }
          }
        }
        let screenWidth = this.$refs.widthIndicator.offsetWidth > minWidth ? this.$refs.widthIndicator.offsetWidth : minWidth
        this.$refs.dndGrid.style.height = minHeight + 'px'
        this.gridWidth = Math.floor(screenWidth / this.colWidth) * this.colWidth
        this.gridColNum = Math.floor(screenWidth / this.colWidth)
      },
      switchPanel(id) {
        if (String(id) === this.id) return // already current panel
        if (!(id > 0)) return // invalid id
        if (parseInt(id) in this.panels)
          this.$router.push({name: 'dashboards', params: {id: String(id)}})
        else
          console.log('no such panel id:', id)
      },
      async loadPanels(update=true) {
        const panels = await panelsList()
        if (!panels) return
        if (panels.errorMessage) {
          this.panelsErrorMessage = panels.errorMessage
          this.panels = null
          return
        } else {
          this.panelsErrorMessage = undefined
          this.panels = panels
          if (parseInt(this.id) in panels && update)
            this.loadWidgetsLayout(parseInt(this.id))
        }
      },
      async addNewPanel(name) {
        let newPanel = await panelCreate(name)
        await this.loadPanels(false)
        this.displayNewPanelPopup = false
        this.newPanelName = ''
        setTimeout(() => {
          this.switchPanel(newPanel.id)
          this.$nextTick(() => this.toggleConfig = true)
        }, 500)
      },
      async duplicatePanel() {
        if (this.panels[this.id] !== undefined) {
          const newPanelId = await duplicatePanel(this.id, this.duplicatePanelName)
          await this.loadPanels(false)
          this.displayDuplicatePanelPopup = false
          this.duplicatePanelName = ""
          setTimeout(() => {
            this.switchPanel(newPanelId)
            // this.$nextTick(() => {this.toggleConfig = true})
          }, 500)
        } else {
          console.error('panel not found:', this.id)
        }
      },
      async delPanel() {
        if (this.panels[this.id] !== undefined) {
          await panelDelete(parseInt(this.id))
          this.loadPanels()
        } else {
          console.log('panel not found:', this.id)
        }
      },
      async loadWidgetsLayout(panelId) {
        this.currentPanel = this.panels[parseInt(panelId)]
        this.toggleConfig = false  // reset panel config switch
        this.panelLoading = true
        this.layout = []
        this.layout = await widgetInstancesList(panelId)
        this.panelLoading = false
      },
      filterDnD(layout) {
        let o = []
        layout.reduce(function (result, item) {
          if (item.wc.widgetName !== 'DNDPlaceholder' && item.x > -1 && item.y > -1) {
            result.push(item)
          }
          return result
        }, o)
        return o
      },
      saveWidgetsLayout() {
        widgetInstancesUpdate(this.filterDnD(this.layout), parseInt(this.id))
        this.updateGrid()
      },
      showGlobalParams() {
        this.widgetGlobalConfig = {params: globalParams}
        this.displayGlobalParams = true
      },
      saveGlobalParams() {
        deepMerge(globalParams, this.widgetGlobalConfig.params)
        saveGlobalParams()
        this.displayGlobalParams = false
      },
    }
  }
</script>

<style lang="css" scoped>
  .shareIcon {
    background-image: url(../assets/micad/images/share-icon.svg);
    content: "";
    position: absolute;
    bottom: 10px;
    right: 10px;
    width: 16px;
    height: 16px;
  }

  .privatePanel {
    background: url(../assets/micad/images/user.svg) no-repeat center;
  }

  .sharedPanel {
    background: url(../assets/sharedPanel-icon.svg) no-repeat center;
  }

  .publicPanel {
    background: url(../assets/micad/images/unlocked.svg) no-repeat center;
  }

  .sharedWithUsersPanel {
    background: url(../assets/micad/images/users.svg) no-repeat center;
  }

  .menuIcon {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    right: 18px;
    width: 16px;
    height: 16px;
    opacity: .5;
  }

  #widgetDragPlaceholder {
    position: absolute;
    z-index: 109;
    background-color: rgba(0, 0, 255, 0.25);
    border: 1px solid #d3d3d3;
    text-align: center;
    display: none;
    width: 400px;
    height: 400px;
  }

  .menuBorderBottom {
    border-bottom: solid 1px #495214 !important;
  }
</style>
